import React, { useState } from 'react';
import ConstellationGlobe from '../components/ConstellationGlobe';

import './ConstellationPage.css';

const PREDEFINED_COLORS = [
  { code: '#FF6347', thumb: '/assets/color_thumbnails/col_FF6347.png' },
  { code: '#1E90FF', thumb: '/assets/color_thumbnails/col_1E90FF.png' },
  { code: '#FFD700', thumb: '/assets/color_thumbnails/col_FFD700.png' },
  { code: '#ADFF2F', thumb: '/assets/color_thumbnails/col_ADFF2F.png' },
  { code: '#8A2BE2', thumb: '/assets/color_thumbnails/col_8A2BE2.png' },
  { code: '#00CED1', thumb: '/assets/color_thumbnails/col_00CED1.png' },
];
const DEFAULT_CONSTELLATION = 'D:550:53:25/5/1';

interface ConstellationInput {
  id: string;
  text: string;
  color: string;
}

const ConstellationPage: React.FC = () => {
  const [showEditor, setShowEditor] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [constellations, setConstellations] = useState<ConstellationInput[]>([{
    id: 'const-0',
    text: DEFAULT_CONSTELLATION,
    color: PREDEFINED_COLORS[0].code,
  }]);
  const [openColorDropdownId, setOpenColorDropdownId] = useState<string | null>(null);

  const toggleColorDropdown = (id: string) => {
    setOpenColorDropdownId(prev => (prev === id ? null : id));
  };

  const toggleEditor = () => setShowEditor(!showEditor);
  const toggleInfo = () => setShowInfo(!showInfo);


  const updateConstellationText = (id: string, newText: string) => {
    setConstellations(prev =>
      prev.map(c => (c.id === id ? { ...c, text: newText } : c))
    );
  };

  const updateConstellationColor = (id: string, newColor: string) => {
    setConstellations(prev =>
      prev.map(c => (c.id === id ? { ...c, color: newColor } : c))
    );
  };

  const addConstellation = () => {
    const nextId = `const-${Date.now()}`;
    setConstellations(prev => [
      ...prev,
      { id: nextId, text: DEFAULT_CONSTELLATION, color: PREDEFINED_COLORS[0].code }
    ]);
  };

  const removeConstellation = (id: string) => {
    setConstellations(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="constellation-page">

      {/* Top-left Controls */}
      <div className="top-left-controls">
        <button
          className="control-button"
          onClick={toggleInfo}
          title="Information"
          style={{ backgroundColor: showInfo ? '#777' : '#333' }}
        >
          I
        </button>
        <button
          className="control-button"
          onClick={toggleEditor}
          title="Edit constellations"
          style={{ backgroundColor: showEditor ? '#777' : '#333' }}
        >
          C
        </button>
      </div>

      <div className="constellation-panels">
        {/* Information Panel */}
        {showInfo && (
          <div className="constellation-info-panel">
            <strong>Constellation Editor</strong>
            <p>
              This tool allows you to create and manage custom constellations.
              Use the editor to define your own satellite constellations and visualize them on the globe.
            </p>
            <p>
              More Information about the constellation code format can be found in the
              <a href="https://github.com/Tim024/ConstellationCode" target="_blank" rel="noopener noreferrer"> repository</a>.
            </p>
          </div>
        )}

        {/* Editor Panel */}
        {showEditor && (
          <div className="constellation-editor-panel">
            <div className="editor-header">
              <strong>Constellations</strong>
              <button className="add-button" onClick={addConstellation}>+</button>
            </div>
            {constellations.map((c, idx) => (
              <div key={c.id} className="editor-row">
                <input
                  type="text"
                  value={c.text}
                  onChange={(e) => updateConstellationText(c.id, e.target.value)}
                  className="constellation-input"
                />
                <div className="color-dropdown-wrapper">
                  <img
                    src={PREDEFINED_COLORS.find(col => col.code === c.color)?.thumb}
                    alt="Selected color"
                    className="selected-color-thumb"
                    onClick={() => toggleColorDropdown(c.id)}
                  />

                  {openColorDropdownId === c.id && (
                    <div className="color-dropdown">
                      {PREDEFINED_COLORS.map(colorObj => (
                        <img
                          key={colorObj.code}
                          src={colorObj.thumb}
                          alt={colorObj.code}
                          className={`color-option-thumb ${c.color === colorObj.code ? 'selected' : ''}`}
                          onClick={() => {
                            updateConstellationColor(c.id, colorObj.code);
                            setOpenColorDropdownId(null); // close dropdown
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="remove-button"
                  onClick={() => removeConstellation(c.id)}
                  title="Remove constellation"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Globe Display */}
      <ConstellationGlobe userDefinedConstellations={constellations} />
    </div>
  );
};

export default ConstellationPage;